<template>
  <div class="prm-tab">
    <div v-if="loading" class="prm-loading">
      <scale-loader color="#3d6b9a"></scale-loader>
    </div>

    <template v-else>
      <!-- Cabecera + metadatos -->
      <prm-template-header ref="header" :meta="data.meta" :reset-key="loadCount" @save-meta="saveMeta"></prm-template-header>

      <!-- Panel de impacto de Change Orders (solo con datos) -->
      <prm-impact-panel v-if="hasData" :impact="impact"></prm-impact-panel>

      <!-- Barra de acciones: contador a la izquierda, acciones a la derecha. -->
      <div class="prm-toolbar">
        <span v-if="dirtyCount" class="edits-badge">{{ dirtyCount }} {{ $t('prm_saved_values') }}</span>
        <button v-if="hasData" class="source-btn" :class="{ active: sourceMode }" @click="sourceMode = !sourceMode">
          <b-icon pack="fas" icon="database" size="is-small"></b-icon>
          <span>{{ $t('prm_summary_source_button') }}</span>
        </button>
        <button class="btn export" :disabled="exporting || !hasData" @click="exportData">
          <b-icon pack="fas" :icon="exporting ? 'spinner' : 'file-excel'" :custom-class="exporting ? 'fa-spin' : ''" size="is-small"></b-icon>
          {{ $t('prm_export_data') }}
        </button>
        <button class="btn" @click="exportTemplate">
          <b-icon pack="fas" icon="file-excel" size="is-small"></b-icon> {{ $t('prm_export_template') }}
        </button>
        <button class="btn import" :disabled="importing" @click="$refs.importInput.click()">
          <b-icon pack="fas" :icon="importing ? 'spinner' : 'file-arrow-up'" :custom-class="importing ? 'fa-spin' : ''" size="is-small"></b-icon>
          {{ $t('prm_import_template') }}
        </button>
        <input ref="importInput" type="file" accept=".xlsx,.xls" class="is-hidden" @change="onImportFile">
        <span class="divider"></span>
        <button class="btn month" @click="addColumn('month')">
          <b-icon pack="fas" icon="calendar-plus" size="is-small"></b-icon> {{ $t('prm_new_month') }}
        </button>
        <button class="btn co" @click="addColumn('co')">
          <b-icon pack="fas" icon="file-circle-plus" size="is-small"></b-icon> {{ $t('prm_new_co') }}
        </button>
        <button class="btn icon-only" :disabled="!canUndo" v-tip="$t('prm_undo_hint')" @click="undo">
          <b-icon pack="fas" icon="rotate-left" size="is-small"></b-icon>
        </button>
        <button class="btn icon-only" :disabled="!canRedo" v-tip="$t('prm_redo_hint')" @click="redo">
          <b-icon pack="fas" icon="rotate-right" size="is-small"></b-icon>
        </button>
      </div>

      <!-- Aviso del modo "Fuente de datos": explica el distintivo de las columnas calculadas. -->
      <div v-if="sourceMode && hasData" class="source-banner">
        <b-icon pack="fas" icon="circle-info" size="is-small"></b-icon>
        <span v-html="$t('prm_source_hint')"></span>
      </div>

      <!-- Tabla -->
      <prm-template-table
        :rows="rows"
        :months="months"
        :cos="cos"
        :computed-map="computedMap"
        :totals="totals"
        :edited="edited"
        :notes="notesMap"
        :observations="obsMap"
        :mode="sourceMode"
        @edit-cell="onEditCell"
        @edit-note="onEditNote"
        @edit-obs="onEditObs"
        @rename-column="renameColumn"
        @remove-column="removeColumn"
        @toggle-co="toggleCo">
      </prm-template-table>
    </template>
  </div>
</template>

<script>
  import axios from 'axios'
  import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
  import PrmTemplateHeader from './template-header.vue'
  import PrmImpactPanel from './impact-panel.vue'
  import PrmTemplateTable from './template-table.vue'
  import { coWeights, computeAll, computeTotals, coSums, flatten } from './template-model'
  import { tip } from '../shared/tip'

  export default {
    name: 'PrmTemplateTab',

    components: { ScaleLoader, PrmTemplateHeader, PrmImpactPanel, PrmTemplateTable },

    directives: { tip },

    props: {
      project: { type: Object, required: true },
    },

    data: () => ({
      loading: true,
      importing: false,
      exporting: false,
      sourceMode: false, // modo "Fuente de datos": marca las columnas calculadas y muestra su fórmula
      history: [],      // pila de snapshots del estado editable (values+meta+columns) para undo/redo
      historyIndex: -1, // posición actual en la pila
      pendingSaves: [], // guardados (celdas/notas/meta) en vuelo, para esperarlos al exportar
      loadCount: 0,   // sube en cada carga → resetea los resaltados de la sesión
      edited: {},     // celdas cambiadas en la sesión (clave `nodeId::colKey` → true)
      data: { meta: {}, columns: { months: [], cos: [] }, structure: [], values: {} },
    }),

    computed: {
      base () {
        return `/api/v2/project/${this.project.id}/dashboard-prm/template`
      },
      months () {
        return this.data.columns.months || []
      },
      cos () {
        return this.data.columns.cos || []
      },
      monthIds () {
        return this.months.map((m) => m.id)
      },
      coIds () {
        return this.cos.map((c) => c.id)
      },
      rows () {
        return flatten(this.data.structure)
      },
      // Sin partidas todavía: la vista muestra el estado vacío (invita a importar).
      hasData () {
        return this.rows.length > 0
      },
      canUndo () { return this.historyIndex > 0 },
      canRedo () { return this.historyIndex < this.history.length - 1 },
      // Cuenta CAMPOS distintos cambiados en la sesión, no las veces que se editó.
      dirtyCount () {
        return Object.keys(this.edited).length
      },
      // Notas por celda aplanadas (`leafId::colKey` → texto) leídas de dentro de values.
      notesMap () {
        const map = {}
        const values = this.data.values || {}
        Object.keys(values).forEach((leafId) => {
          const n = values[leafId] && values[leafId].n
          if (n) Object.keys(n).forEach((k) => { map[leafId + '::' + k] = n[k] })
        })
        return map
      },
      // Observaciones por hoja (leafId → texto), leídas de dentro de values.
      obsMap () {
        const map = {}
        const values = this.data.values || {}
        Object.keys(values).forEach((leafId) => {
          if (values[leafId] && values[leafId].obs) map[leafId] = values[leafId].obs
        })
        return map
      },
      // Los totales computan SOLO las COs confirmadas (regla de control económico).
      computedMap () {
        const weights = coWeights(this.cos, 'confirmed')
        return computeAll(this.data.structure, this.data.values, this.monthIds, this.coIds, weights)
      },
      totals () {
        return computeTotals(this.computedMap, this.data.structure, this.monthIds, this.coIds)
      },
      // Cada caja muestra su sumatoria de COs (confirmadas, pendientes y total) con su recuento.
      // Incluye la fila de Ventas (VENTAS/POC) además del lado coste (coSums).
      impact () {
        const isExpected = (c) => (c.status || 'confirmed') === 'expected'
        const { confirmed, expected, both } = coSums(this.totals, this.cos)
        const expectedCount = this.cos.filter(isExpected).length
        const confirmedCount = this.cos.length - expectedCount

        return {
          expectedCount,
          cards: [
            { id: 'confirmed', label: this.$t('prm_only_confirmed'), value: confirmed, count: confirmedCount },
            { id: 'expected', label: this.$t('prm_only_pending'), value: expected, count: expectedCount },
            { id: 'both', label: this.$t('prm_confirmed_plus_pending'), value: both, count: this.cos.length },
          ],
        }
      },
    },

    watch: {
      'project.id': { immediate: true, handler () { this.load() } },
    },

    mounted () {
      window.addEventListener('keydown', this.onKeydown)
    },
    beforeDestroy () {
      window.removeEventListener('keydown', this.onKeydown)
    },

    methods: {
      // Atajos: Ctrl/Cmd+Z = deshacer · Ctrl/Cmd+Shift+Z o Ctrl+Y = rehacer. No actuamos si
      // el tab está oculto (otro tab activo) ni si se está escribiendo en un campo.
      onKeydown (e) {
        if (!(e.ctrlKey || e.metaKey)) return
        if (!this.$el || this.$el.offsetParent === null) return
        const t = e.target
        const tag = t && t.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return
        const key = (e.key || '').toLowerCase()
        if (key === 'z' && !e.shiftKey) { e.preventDefault(); this.undo() }
        else if ((key === 'z' && e.shiftKey) || key === 'y') { e.preventDefault(); this.redo() }
      },

      async load () {
        this.loading = true
        this.edited = {}     // nueva sesión: se limpian los resaltados de celdas
        this.loadCount++     // y los de la cabecera (vía reset-key)
        try {
          const { data } = await axios.get(this.base)
          this.data = data.data
          // Arrancamos el historial en el estado cargado (punto 0 del undo/redo).
          this.history = [this.snapshotState()]
          this.historyIndex = 0
        } catch (e) {
          this.$notify.error((e && e.error_msg) || this.$t('error_retrieving_data'))
        } finally {
          this.loading = false
        }
      },

      // Importa una plantilla Excel (nuestro formato) y reemplaza el contenido.
      onImportFile (e) {
        const file = e.target.files && e.target.files[0]
        e.target.value = '' // permite reimportar el mismo archivo
        if (!file || this.importing) return
        this.importing = true
        const form = new FormData()
        form.append('file', file)
        axios.post(`${this.base}/import`, form)
          .then(() => {
            this.$notify.success(this.$t('prm_import_success'))
            this.$emit('imported') // avisamos para que los demás tabs (Resumen) recarguen sus datos del PRM
            return this.load() // load() ya resetea los resaltados de la sesión
          })
          .catch((err) => this.$notify.error((err && err.error_msg) || this.$t('prm_import_unrecognized')))
          .finally(() => { this.importing = false })
      },

      // Exporta los datos actuales a xlsx (nuestro formato, con columnas calculadas).
      async exportData () {
        if (this.exporting) return
        this.exporting = true
        try {
          // Vuelca los guardados de cabecera pendientes (debounce) y espera a que TODOS
          // los guardados en vuelo terminen, para que el export lea el estado real.
          if (this.$refs.header) this.$refs.header.flush()
          await Promise.allSettled(this.pendingSaves)

          const response = await axios.get(`${this.base}/export`, { responseType: 'blob' })
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.download = `PRM - ${this.project.name || 'export'}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        } catch (e) {
          this.$notify.error(this.$t('error_retrieving_data'))
        } finally {
          this.exporting = false
        }
      },

      // Descarga la plantilla en blanco (fichero público) para rellenar offline.
      // Resolvemos con assetVapor (mismo patrón que el resto de plantillas del repo):
      // en prod el fichero se sirve desde el CDN de Vapor, no desde la app.
      exportTemplate () {
        const link = document.createElement('a')
        link.href = this.assetVapor('template_import_prm.xlsx')
        link.download = 'Plantilla PRM.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      },

      // ── Historial (undo/redo) ──
      // Snapshot del estado editable actual: valores, meta y columnas (meses/COs).
      snapshotState () {
        return {
          values: JSON.parse(JSON.stringify(this.data.values || {})),
          meta: JSON.parse(JSON.stringify(this.data.meta || {})),
          columns: JSON.parse(JSON.stringify(this.data.columns || { months: [], cos: [] })),
        }
      },
      // Registra el estado actual como nuevo punto del historial (tras cada cambio real).
      recordHistory () {
        // Si veníamos de deshacer, la rama de rehacer se descarta al hacer un cambio nuevo.
        if (this.historyIndex < this.history.length - 1) {
          this.history = this.history.slice(0, this.historyIndex + 1)
        }
        this.history.push(this.snapshotState())
        this.historyIndex = this.history.length - 1
        // Acotamos el tamaño para no crecer sin fin.
        const MAX = 100
        while (this.history.length > MAX) {
          this.history.shift()
          this.historyIndex--
        }
      },
      undo () {
        if (!this.canUndo) return
        this.historyIndex--
        this.applyHistory()
      },
      redo () {
        if (!this.canRedo) return
        this.historyIndex++
        this.applyHistory()
      },
      // Aplica el snapshot actual del historial: estado local + persistencia en una sola operación.
      applyHistory () {
        const snap = this.history[this.historyIndex]
        this.$set(this.data, 'values', JSON.parse(JSON.stringify(snap.values)))
        this.$set(this.data, 'meta', JSON.parse(JSON.stringify(snap.meta)))
        this.$set(this.data, 'columns', JSON.parse(JSON.stringify(snap.columns)))
        this.edited = {} // navegar el historial invalida los resaltados de la sesión
        this.loadCount++ // y los de la cabecera
        this.persist(axios.post(`${this.base}/restore`, { values: snap.values, meta: snap.meta, columns: snap.columns })
          .catch(() => this.$notify.error(this.$t('error_saving_data'))))
      },

      // Autosave de una celda: actualiza el estado local y persiste en segundo plano.
      // Solo llega aquí en cambios REALES (la celda no emite si el valor no cambió),
      // así que cada llamada persiste de verdad. El contador marca el campo (una vez
      // por sesión, aunque se edite varias veces) y lo resalta.
      onEditCell ({ leafId, field, colId, value, key }) {
        // Sin ec/ep por defecto: una entrada nueva no debe nacer con ceros (serían "valor").
        const entry = { ...(this.data.values[leafId] || { m: {}, co: {} }) }
        if (['ec', 'ep', 'it', 'ac'].includes(field)) {
          entry[field] = value // null = celda vaciada (sin dato)
        } else {
          entry[field] = { ...(entry[field] || {}), [colId]: value }
        }
        this.$set(this.data.values, leafId, entry)
        this.$set(this.edited, key, true)
        this.recordHistory()
        this.persist(axios.put(`${this.base}/cell`, { leafId, field, colId, value })
          .catch(() => this.$notify.error(this.$t('error_saving_data'))))
      },

      // Guarda (o borra) las Observaciones de una hoja: texto libre en values[leafId].obs.
      onEditObs ({ leafId, text, key }) {
        const entry = { ...(this.data.values[leafId] || { m: {}, co: {} }) }
        const clean = (text || '').trim()
        if (clean) entry.obs = clean
        else delete entry.obs
        this.$set(this.data.values, leafId, entry)
        if (key) this.$set(this.edited, key, true)
        this.recordHistory()
        this.persist(axios.put(`${this.base}/cell`, { leafId, field: 'obs', text: clean })
          .catch(() => this.$notify.error(this.$t('error_saving_data'))))
      },

      // Guarda (o borra) la descripción de una celda: vive dentro de values[leafId].n,
      // y se persiste por la MISMA ruta que el valor (el backend detecta `note`).
      onEditNote ({ leafId, field, colId, note }) {
        const colKey = ['ec', 'ep', 'it', 'ac'].includes(field) ? field : field + ':' + colId
        const entry = { ...(this.data.values[leafId] || { m: {}, co: {} }) }
        const n = { ...(entry.n || {}) }
        if (note) n[colKey] = note
        else delete n[colKey]
        entry.n = n
        this.$set(this.data.values, leafId, entry)
        this.recordHistory()
        this.persist(axios.put(`${this.base}/cell`, { leafId, field, colId, note })
          .catch(() => this.$notify.error(this.$t('error_saving_data'))))
      },

      // Alta optimista: el id se genera en cliente y la columna aparece al instante;
      // el POST viaja en segundo plano y solo revierte si falla.
      addColumn (kind) {
        const bucket = kind === 'co' ? 'cos' : 'months'
        const id = (kind === 'co' ? 'co-' : 'm-') + this.uid()
        const column = kind === 'co'
          ? { id, label: `CO${this.cos.length + 1}`, status: 'expected', order: this.cos.length }
          : { id, label: this.$t('prm_new_month'), order: this.months.length }
        this.data.columns[bucket].push(column)
        this.recordHistory()
        axios.post(`${this.base}/column`, { kind, ...column }).catch(() => {
          this.data.columns[bucket] = this.data.columns[bucket].filter((c) => c.id !== id)
          this.$notify.error(this.$t('error_saving_data'))
        })
      },

      uid () {
        return Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
      },

      renameColumn ({ id, label }) {
        this.mutateColumn(id, (c) => { c.label = label })
        this.recordHistory()
        axios.put(`${this.base}/column/${id}`, { label }).catch(() => this.$notify.error(this.$t('error_saving_data')))
      },

      toggleCo (id) {
        let status
        this.mutateColumn(id, (c) => { c.status = status = (c.status === 'confirmed' ? 'expected' : 'confirmed') })
        this.recordHistory()
        axios.put(`${this.base}/column/${id}`, { status }).catch(() => this.$notify.error(this.$t('error_saving_data')))
      },

      removeColumn (id) {
        this.data.columns.months = this.months.filter((c) => c.id !== id)
        this.data.columns.cos = this.cos.filter((c) => c.id !== id)
        this.recordHistory()
        axios.delete(`${this.base}/column/${id}`).catch(() => this.$notify.error(this.$t('error_saving_data')))
      },

      saveMeta (changes) {
        // Fusiona firmas campo a campo (igual que el backend); el resto reemplaza.
        const meta = { ...this.data.meta }
        Object.keys(changes).forEach((k) => {
          if (k === 'signatures') meta.signatures = { ...(meta.signatures || {}), ...changes.signatures }
          else meta[k] = changes[k]
        })
        this.data.meta = meta
        this.recordHistory()
        this.persist(axios.put(`${this.base}/meta`, changes).catch(() => this.$notify.error(this.$t('error_saving_data'))))
      },

      // Rastrea un guardado en vuelo para poder esperarlo (p. ej. antes de exportar).
      persist (promise) {
        this.pendingSaves.push(promise)
        const done = () => { this.pendingSaves = this.pendingSaves.filter((p) => p !== promise) }
        promise.then(done, done)
        return promise
      },

      // Aplica un cambio a una columna (mes o CO) en el estado local.
      mutateColumn (id, fn) {
        const col = [...this.months, ...this.cos].find((c) => c.id === id)
        if (col) fn(col)
      },
    },
  }
</script>

<style lang="scss" scoped>
  // flex-column + min-width:0 evita que la tabla ancha estire cabecera/panel/toolbar
  // (la columna de contenido del layout es un item flex con min-width:auto).
  .prm-tab { display: flex; flex-direction: column; min-width: 0; padding: 4px 0; }
  .prm-loading { display: flex; justify-content: center; padding: 48px 0; }

  .prm-toolbar {
    display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 8px;
    border: 1px solid #d4dae3; border-bottom: 0; background: #fff; padding: 8px 16px;
  }
  // El contador queda pegado a la izquierda; las acciones se agrupan a la derecha.
  .edits-badge { margin-right: auto; font-size: 10px; font-weight: 600; border-radius: 3px; padding: 2px 8px; background: #fff3cd; color: #8a6d00; }
  // Toggle "Fuente de datos": distintivo ámbar, igual que en los tabs Dashboard y Resumen.
  .source-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid #e0b52a; background: #fff; color: #8a6d00;
    font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 4px; cursor: pointer;
  }
  .source-btn.active { background: #fff4d6; }
  .source-btn.active:hover { background: #fbecc0; }
  .divider { width: 1px; align-self: stretch; margin: 2px 2px; background: #d4dae3; }
  .btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid #c3ccd8; border-radius: 4px; background: #fff;
    padding: 4px 10px; font-size: 11px; font-weight: 500; color: #33475f; cursor: pointer;
  }
  // Botones solo-icono (deshacer/rehacer): cuadrados y algo más compactos.
  .btn.icon-only { padding: 4px 9px; }
  .btn:disabled { opacity: .45; cursor: not-allowed; }
  .btn.export { background: #eef7f0; border-color: #a9d0b4; color: #1e6b3d; }
  .btn.import { background: #eef3fa; border-color: #b3c6df; color: #274b7a; }
  .btn.month { background: #fffdf5; }
  .btn.co { background: #fdf6f0; }
  // Hover: un tono algo más profundo dentro de la misma familia de color
  // (nada de brillo/gris plano) para que todos se sientan iguales.
  .btn:hover { border-color: #9fb0c4; background: #eef1f5; }
  .btn.export:hover { background: #e2efe7; }
  .btn.import:hover { background: #e2eaf6; }
  .btn.month:hover { background: #f8f3e4; }
  .btn.co:hover { background: #f6eadf; }

  // Aviso del modo "Fuente de datos": banda ámbar bajo la toolbar (mismo estilo que los otros tabs).
  .source-banner {
    display: flex; align-items: flex-start; gap: 8px;
    border: 1px solid #e0b52a; border-top: 0; background: #fff4d6; color: #8a6d00;
    padding: 8px 16px; font-size: 11px; line-height: 1.45;
  }
  .source-banner ::v-deep b { font-weight: 700; }
</style>
